import type { NavAccess } from '@/hooks/use-nav-visibility';

/**
 * Satu langkah alur. Hak lihatnya (`permission`, `graduatingOnly`, dst.)
 * dipegang langkah itu sendiri, dengan arti yang sama seperti di butir menu:
 * tidak semua langkah punya menu sendiri di sidebar — SKTB hanya punya satu
 * pintu untuk empat langkah.
 */
export interface WorkflowStep extends NavAccess {
  /** Nama pendek di stepper; menu sidebar boleh bernama lebih panjang. */
  label: string;
  /** Satu frasa: apa yang dikerjakan di langkah ini. */
  hint: string;
}

export interface Workflow {
  key: string;
  title: string;
  steps: WorkflowStep[];
  /**
   * Alamat di bawah salah satu langkah yang tidak memuat stepper — lembar
   * cetak yang memenuhi layar, misalnya, tidak boleh didorong ke bawah.
   */
  hiddenOn?: string[];
}

/**
 * Alur kerja yang dipandu stepper di atas halaman.
 *
 * Rapor dan SKTB tersebar di belasan menu Akademik, dan dari sidebar saja
 * guru tidak bisa membaca mana yang harus diisi lebih dulu. Stepper ini tidak
 * membuat halaman baru — ia hanya menautkan halaman yang sudah ada dalam urutan
 * pengerjaannya. Hak lihat tiap langkah sama dengan halaman/menunya, jadi
 * langkah yang tidak boleh dibuka ikut hilang.
 */
export const WORKFLOWS: Workflow[] = [
  {
    key: 'rapor',
    title: 'Alur Penyusunan Rapor',
    steps: [
      {
        href: '/rapor/setelan',
        label: 'Setelan Rapor',
        hint: 'Bentuk lembar rapor',
        // Menulis ke data satuan — hanya yang boleh mengubah Sekolah.
        permission: 'school.update',
      },
      {
        href: '/rapor-penilaian/rencana',
        label: 'Rencana Pembelajaran',
        hint: 'TP, ATP & aktivitas',
        permission: 'learningObjectiveFlow.read',
      },
      {
        href: '/kokurikuler',
        label: 'Kegiatan Kokurikuler',
        hint: 'Rancang kegiatannya',
        permission: 'cocurricular.read',
      },
      {
        href: '/kehadiran',
        label: 'Kehadiran',
        hint: 'Rekap masuk ke rapor',
        permission: 'attendance.read',
      },
      {
        // Penilaian sebelum rapor ada dua, dan keduanya bukan pengamatan
        // harian bertanggal: capaian ATP dinilai di akhir Aktivitas, capaian
        // kokurikuler di akhir kegiatan.
        href: '/rapor-penilaian',
        label: 'Penilaian Intrakurikuler',
        hint: 'Capaian ATP per aktivitas',
        permission: 'learningActivity.read',
      },
      {
        href: '/rapor-penilaian/kokurikuler',
        label: 'Penilaian Kokurikuler',
        hint: 'Capaian per kegiatan',
        permission: 'cocurricular.read',
      },
      {
        href: '/rapor',
        label: 'Susun & Cetak',
        hint: 'Deskripsi lalu cetak',
        permission: 'reportCard.read',
      },
    ],
    hiddenOn: ['/rapor/cetak', '/kehadiran/rekap/cetak'],
  },
  {
    key: 'sktb',
    title: 'Alur Penyusunan SKTB',
    steps: [
      {
        href: '/sktb/setelan',
        label: 'Setelan SKTB',
        hint: 'Kop & kebijakan lulus',
        // Menulis ke data satuan — hanya yang boleh mengubah Sekolah.
        permission: 'school.update',
      },
      {
        href: '/sktb/calon-kelulusan',
        label: 'Calon Kelulusan',
        hint: 'Nomor & tanggal',
        permission: 'sktb.read',
        graduatingOnly: true,
      },
      {
        // Penilaian jenjang lain tempatnya di Penilaian Intrakurikuler alur
        // Rapor (learningActivity), bukan di sini.
        href: '/penilaian',
        label: 'Penilaian SKTB',
        hint: 'Capaian yang tercetak',
        permission: 'assessment.read',
        graduatingOnly: true,
      },
      {
        href: '/sktb/cetak',
        label: 'Cetak SKTB',
        hint: 'Cetak lembar SKTB',
        permission: 'sktb.read',
        graduatingOnly: true,
      },
    ],
  },
];

function matchesStep(pathname: string, step: WorkflowStep) {
  return pathname === step.href || pathname.startsWith(`${step.href}/`);
}

/**
 * Alur dan langkah yang sedang dibuka.
 *
 * Langkah dengan alamat terpanjang yang cocok yang menang, supaya
 * `/rapor/setelan` jatuh ke Setelan Rapor dan bukan ke `/rapor`, sementara
 * editor `/rapor/:studentId` tetap terhitung langkah Susun & Cetak.
 */
export function findWorkflowStep(pathname: string) {
  let best: { workflow: Workflow; step: WorkflowStep } | null = null;

  for (const workflow of WORKFLOWS) {
    if (workflow.hiddenOn?.some((path) => pathname === path || pathname.startsWith(`${path}/`))) {
      continue;
    }
    for (const step of workflow.steps) {
      if (matchesStep(pathname, step) && (!best || step.href.length > best.step.href.length)) {
        best = { workflow, step };
      }
    }
  }

  return best;
}
